import type { ComponentType } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { I18nProvider } from '@/components/I18nProvider';
import { SessionProvider } from '@/components/SessionProvider';
import { SettingsProvider } from '@/components/SettingsProvider';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { AccountPage } from '@/pages/public/AccountPage';
import { EventPage } from '@/pages/public/EventPage';
import { HomePage } from '@/pages/public/HomePage';
import { NotFoundPage } from '@/pages/public/NotFoundPage';
import { OrderPage } from '@/pages/public/OrderPage';

/** Verwaltung und Einlass werden erst bei Bedarf geladen – der Shop bleibt schlank. */
function lazyComponent<M>(load: () => Promise<M>, pick: (module: M) => ComponentType) {
  return async () => ({ Component: pick(await load()) });
}

const websiteEntries = () => import('@/pages/admin/website/EntriesPage');
const websiteEditor = () => import('@/pages/admin/website/EntryEditPage');

/**
 * Die Website selbst (/, /beitraege/, …) sind vorab erzeugte HTML-Seiten. Diese App übernimmt den
 * Ticketshop unter /tickets, die Verwaltung unter /admin und den Einlass unter /einlass.
 */
const router = createBrowserRouter([
  {
    path: 'tickets',
    element: <PublicLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'veranstaltung/:id', element: <EventPage /> },
      { path: 'bestellung/:id', element: <OrderPage /> },
      { path: 'konto', element: <AccountPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: 'admin/callback',
    lazy: lazyComponent(() => import('@/pages/admin/AdminCallbackPage'), (module) => module.AdminCallbackPage),
  },
  {
    path: 'admin',
    lazy: lazyComponent(() => import('@/pages/admin/AdminLayout'), (module) => module.AdminLayout),
    children: [
      { index: true, lazy: lazyComponent(() => import('@/pages/admin/AdminIndexPage'), (module) => module.AdminIndexPage) },
      // Tickets
      { path: 'veranstaltungen', lazy: lazyComponent(() => import('@/pages/admin/EventsPage'), (module) => module.EventsPage) },
      { path: 'veranstaltungen/neu', lazy: lazyComponent(() => import('@/pages/admin/EventEditPage'), (module) => module.NewEventPage) },
      { path: 'veranstaltungen/:id', lazy: lazyComponent(() => import('@/pages/admin/EventEditPage'), (module) => module.EditEventPage) },
      {
        path: 'veranstaltungen/:id/abendkasse',
        lazy: lazyComponent(() => import('@/pages/admin/BoxOfficePage'), (module) => module.BoxOfficePage),
      },
      {
        path: 'veranstaltungen/:id/bestellungen',
        lazy: lazyComponent(() => import('@/pages/admin/EventOrdersPage'), (module) => module.EventOrdersPage),
      },
      { path: 'bestellungen', lazy: lazyComponent(() => import('@/pages/admin/OrderSearchPage'), (module) => module.OrderSearchPage) },
      { path: 'bestellungen/:id', lazy: lazyComponent(() => import('@/pages/admin/OrderDetailPage'), (module) => module.OrderDetailPage) },
      { path: 'geraete', lazy: lazyComponent(() => import('@/pages/admin/DevicesPage'), (module) => module.DevicesPage) },
      // Website
      { path: 'beitraege', lazy: lazyComponent(websiteEntries, (module) => module.PostsPage) },
      { path: 'beitraege/neu', lazy: lazyComponent(websiteEditor, (module) => module.NewPostPage) },
      { path: 'beitraege/:id', lazy: lazyComponent(websiteEditor, (module) => module.EditPostPage) },
      { path: 'seiten', lazy: lazyComponent(websiteEntries, (module) => module.PagesPage) },
      { path: 'seiten/neu', lazy: lazyComponent(websiteEditor, (module) => module.NewPagePage) },
      { path: 'seiten/:id', lazy: lazyComponent(websiteEditor, (module) => module.EditPagePage) },
      {
        path: 'website',
        lazy: lazyComponent(() => import('@/pages/admin/website/HomeSettingsPage'), (module) => module.HomeSettingsPage),
      },
      // Verein
      { path: 'benutzer', lazy: lazyComponent(() => import('@/pages/admin/UsersPage'), (module) => module.UsersPage) },
      { path: 'einstellungen', lazy: lazyComponent(() => import('@/pages/admin/SettingsPage'), (module) => module.SettingsPage) },
    ],
  },
  { path: 'einlass', lazy: lazyComponent(() => import('@/scanner/ScannerPage'), (module) => module.ScannerPage) },
]);

export function App() {
  return (
    <I18nProvider>
      <SessionProvider>
        <SettingsProvider>
          <RouterProvider router={router} />
        </SettingsProvider>
      </SessionProvider>
    </I18nProvider>
  );
}
